import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { EmptyState, LoadingState } from '@/components/ui'
import { cn } from '@/lib/utils.ts'

interface ReportItem {
  id: string
  contractNumber: string
  title: string | null
  customer: { name: string | null }
  endDate: string
  daysRemaining: number
  autoRenew: boolean
  noticePeriodDays: number
}

interface ExpiryReport {
  critical: ReportItem[]
  warning: ReportItem[]
  upcoming: ReportItem[]
  summary: { total: number; critical: number; warning: number; upcoming: number }
}

function urgencyOf(item: ReportItem): 'critical' | 'warning' | 'upcoming' {
  if (item.daysRemaining <= 14) return 'critical'
  if (item.daysRemaining <= 45) return 'warning'
  return 'upcoming'
}

const URGENCY_STYLES = {
  critical: { row: 'bg-red-50 hover:bg-red-100', badge: 'bg-red-100 text-red-600', label: 'text-red-600' },
  warning: { row: 'bg-yellow-50 hover:bg-yellow-100', badge: 'bg-yellow-100 text-yellow-600', label: 'text-yellow-600' },
  upcoming: { row: 'hover:bg-gray-50', badge: 'bg-gray-100 text-gray-600', label: 'text-gray-600' },
}

export default function ExpiringContractsWidget({ onRefresh }: { onRefresh?: () => void }) {
  const navigate = useNavigate()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['contracts-expiry-report'],
    queryFn: async () => {
      const { data } = await apiClient.get<ExpiryReport>('/contracts/expiry-report?days=90')
      return data
    },
    refetchInterval: 5 * 60 * 1000,
  })

  const allItems = data
    ? [...data.critical, ...data.warning, ...data.upcoming].slice(0, 10)
    : []

  const handleRefresh = () => {
    void refetch()
    onRefresh?.()
  }

  if (isLoading) return <LoadingState size="sm" label="" />

  if (!data || allItems.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <EmptyState size="sm" title="Keine Verträge laufen in den nächsten 90 Tagen ab" />
        </div>
        <FooterLink />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-1.5 flex items-center justify-between border-b border-gray-100">
        <span className="text-caption text-gray-500">
          {data.summary.total} Vertrag{data.summary.total !== 1 ? 'e' : ''} im Blick
        </span>
        <button
          type="button"
          onClick={handleRefresh}
          className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Aktualisieren"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
        {allItems.map((item) => {
          const urgency = urgencyOf(item)
          const styles = URGENCY_STYLES[urgency]
          return (
            <ContractRow
              key={item.id}
              item={item}
              rowClass={styles.row}
              badgeClass={styles.badge}
              labelClass={styles.label}
              onClick={() => navigate(`/vertraege/${item.id}`)}
            />
          )
        })}
      </div>

      <FooterLink />
    </div>
  )
}

function ContractRow({
  item,
  rowClass,
  badgeClass,
  labelClass,
  onClick,
}: {
  item: ReportItem
  rowClass: string
  badgeClass: string
  labelClass: string
  onClick: () => void
}) {
  const endDate = new Date(item.endDate).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={cn('px-3 py-2 cursor-pointer transition-colors focus:outline-none', rowClass)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className={cn("text-body font-semibold truncate", labelClass)}>
            {item.title ?? item.contractNumber}
          </p>
          {item.customer.name && (
            <p className="text-caption text-gray-500 truncate">{item.customer.name}</p>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-caption text-gray-400">{endDate}</span>
            {item.autoRenew && (
              <span className="text-caption text-blue-500" title="Auto-Verlängerung aktiv">↻</span>
            )}
          </div>
        </div>
        <span className={cn('flex-shrink-0 rounded-full px-2 py-0.5 text-caption font-semibold', badgeClass)}>
          {item.daysRemaining}d
        </span>
      </div>
    </div>
  )
}

function FooterLink() {
  const navigate = useNavigate()
  return (
    <div className="border-t border-gray-100">
      <button
        type="button"
        onClick={() => navigate('/vertraege')}
        className="w-full px-4 py-2 text-caption text-brand-600 hover:text-brand-700 hover:underline text-left transition-colors"
      >
        Alle Verträge anzeigen →
      </button>
    </div>
  )
}
